// frontend/src/pages/Upgrade.jsx
import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { getAllPlanForUnAuth } from "../api/apiFunction/planServices";
// Two-provider payment flow (Tap/Stripe) - keep for future use
// Temporarily comment out unused import to avoid lint errors
// import { createCheckoutSession } from "../api/apiFunction/paymentServices";
// This is kept for future subscription flows; trial now requires card details
import { createSubscriptionCheckout } from "../api/apiFunction/paymentServices";
// Temporarily comment out unused PaymentModal import; usage remains commented below
// import PaymentModal from "../components/pages/upgrade/PaymentModal";
import { getReferralById } from "../api/apiFunction/referralServices";
import { useNavigate } from "react-router-dom";
// import activateTrial API to start free trial from Upgrade page
// import getTrialDetails to use server-side trial status
import {
  activateTrial,
  getTrialDetails,
} from "../api/apiFunction/userServices";
// import react-toastify to show toasts consistently across app
import { toast } from "react-toastify";
// import LogoWithText to use consistent branding across the app
import { LogoWithText } from "../components/Logo";

const Upgrade = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  // Removed redirecting state; trial flow handles redirect directly
  // track free trial activation button/loading state
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loadingDiscount, setLoadingDiscount] = useState(false);
  // Two-provider modal toggle (kept for future enablement)
  // const [showPaymentModal, setShowPaymentModal] = useState(false);

  // This toggles the card processing modal
  // const [showCardModal, setShowCardModal] = useState(false);

  // Full page loading during redirect
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Card form fields
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardCountry, setCardCountry] = useState("");
  const [cardCity, setCardCity] = useState("");
  const [cardStreet, setCardStreet] = useState("");
  const [cardZip, setCardZip] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await getAllPlanForUnAuth();
        if (res?.data) {
          setPlans(res.data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Fetch referral discount data
  useEffect(() => {
    const fetchReferralDiscount = async () => {
      const userObj = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      const userId = userObj?._id || userObj?.id || null;

      if (userId) {
        try {
          const res = await getReferralById(userId);
          if (res?.data && res.data.length > 0) {
            // Get the discount amount from the first referral
            const discount = res.data[0]?.discount_amount || 0;
            setDiscountAmount(discount);
            // Store referralId if it exists in the response
            // Referral id no longer required for trial activation
          }
        } catch (error) {
          setDiscountAmount(0);
        } finally {
          setLoadingDiscount(false);
        }
      } else {
        setLoadingDiscount(false);
      }
    };
    fetchReferralDiscount();
  }, []);

  // call trial details API; redirect to chat for progress/paid; keep pending on upgrade
  useEffect(() => {
    const checkTrialAndRedirect = async () => {
      const localUser = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      if (!localUser) return;

      try {
        const res = await getTrialDetails();
        const serverUser = res?.data?.user || null;
        const trial = serverUser?.trial || localUser?.trial || null;

        const mergedUser = { ...localUser, trial };
        localStorage.setItem("user", JSON.stringify(mergedUser));

        if (mergedUser.role === "individual") {
          const status = trial?.status;
          if (status === "progress" || status === "paid") {
            navigate("/chat", { replace: true });
          }
          // pending stays on upgrade
          return;
        }
        // Admins always go to org dashboard
        if (mergedUser.role === "admin") {
          navigate("/org/dashboard", { replace: true });
          return;
        }
        // Employees with a plan go to chat; otherwise remain on upgrade
        if (mergedUser.role === "employee" && mergedUser.plan) {
          navigate("/chat", { replace: true });
          return;
        }
      } catch {
        // fallback to localStorage-based decision if API fails
        if (localUser.role === "individual") {
          const status = localUser?.trial?.status;
          if (status === "progress" || status === "paid") {
            navigate("/chat", { replace: true });
          }
          return;
        }
        if (localUser.role === "admin") {
          navigate("/org/dashboard", { replace: true });
          return;
        }
        if (localUser.role === "employee" && localUser.plan) {
          navigate("/chat", { replace: true });
          return;
        }
      }
    };
    checkTrialAndRedirect();
  }, [navigate]);

  // Get user type from localStorage and filter plans accordingly
  const userObj = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const userRole = userObj?.role || "individual";
  // derive trial visibility for free trial/skip buttons based on trial status
  const trial = userObj?.trial || null;
  const showFreeTrialButton =
    userRole === "individual" &&
    trial?.status !== "expired" &&
    trial?.status !== "cancelled";
  const showExpiredTrialButton =
    userRole === "individual" &&
    (trial?.status === "expired" || trial?.status === "cancelled");
  // Skip Trial button removed; rely on Start Trial and Subscribe only

  // Show organization plan for employees, individual plan for others
  const planType = userRole === "employee" ? "organization" : "individual";
  const filteredPlans = plans.filter((plan) => plan.planType === planType);
  const plan = filteredPlans[0];

  // Calculate discounted price if discount exists
  const originalPrice = plan?.baseCost || 0;
  const discountedPrice = Math.max(0, originalPrice - discountAmount);
  const hasDiscount = discountAmount > 0;

  // Helper function to format limit value
  const formatLimit = (value) => {
    if (value === -1 || value === "Unlimited") return "Unlimited";
    return typeof value === "number" ? value.toLocaleString() : value || "N/A";
  };

  // Manually format plan features for display
  const features = plan
    ? [
        {
          label: "High-end tokens",
          daily: formatLimit(plan.dailyLimits?.highEndLlmTokens),
          monthly: formatLimit(plan.monthlyLimits?.highEndLlmTokens),
        },
        {
          label: "Low-end tokens",
          daily: formatLimit(plan.dailyLimits?.smallLlmTokens),
          monthly: formatLimit(plan.monthlyLimits?.smallLlmTokens),
        },
        {
          label: "Image generations",
          daily: formatLimit(plan.dailyLimits?.images),
          monthly: formatLimit(plan.monthlyLimits?.images),
        },
        {
          label: "Web searches",
          daily: formatLimit(plan.dailyLimits?.webSearches),
          monthly: formatLimit(plan.monthlyLimits?.webSearches),
        },
      ]
    : [];

  // show a toast using react-toastify so it appears globally
  // this uses ToastContainer configured in App.jsx
  const showToastInfo = (message) => toast.info(message);
  const showToastSuccess = (message) => toast.success(message);
  const showToastError = (message) => toast.error(message);

  // Get userId and user info from localStorage
  // userId not required for trial activation payload
  const userName = userObj?.name || "";

  // Removed paid subscription upgrade; trial now handles activation via payment auth

  // start checkout using the selected payment provider (Tap/Stripe)
  // kept for future, commented to avoid lint errors until modal is re-enabled
  // const handleProviderSelection = async (selectedProvider) => {
  //   try {
  //     setIsRedirecting(true);
  //     setShowPaymentModal(false);
  //
  //     // Use new dual provider checkout
  //     const res = await createSubscriptionCheckout(
  //       plan._id,
  //       userId,
  //       referralId,
  //       selectedProvider,
  //       "monthly"
  //     );
  //
  //     if (res.data && res.data.checkoutUrl) {
  //       if (res.data.sessionId) {
  //         localStorage.setItem("payment_session_id", res.data.sessionId);
  //       }
  //       window.location.href = res.data.checkoutUrl;
  //     } else {
  //       alert("Failed to initiate payment.");
  //       setIsRedirecting(false);
  //     }
  //   } catch (err) {
  //     alert("Payment initiation failed.");
  //     setIsRedirecting(false);
  //   }
  // };

  // Parse combined expiry field (MM/YY format)
  const parseCardExpiry = (expiryStr) => {
    const parts = expiryStr.split("/");
    return {
      month: parts[0]?.trim() || "",
      year: parts[1]?.trim() || "",
    };
  };

  // Validate card form
  const canSubmitCard = () => {
    const { month, year } = parseCardExpiry(cardExpiry);
    const mm = parseInt(month, 10);
    return (
      cardNumber &&
      cardExpiry &&
      cardCvc &&
      mm >= 1 &&
      mm <= 12 &&
      year?.length === 2
    );
  };

  // Auto-format expiry date to MM/YY format
  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits

    // Limit to 4 digits
    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    // Auto-insert slash after MM
    if (value.length >= 2) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }

    setCardExpiry(value);
  };

  // handle starting a free trial from the upgrade page using form fields
  const handleActivateTrial = async (e) => {
    e?.preventDefault?.();

    if (!canSubmitCard()) {
      showToastError("Please fill in all required card fields correctly.");
      return;
    }

    // ensure a user object exists; token usage is handled by http methods
    const localUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (!localUser) {
      alert("Please sign in to start your free trial.");
      window.location.href = "/login";
      return;
    }

    setIsActivatingTrial(true);
    setIsPageLoading(true);

    try {
      // Parse expiry
      const { month, year } = parseCardExpiry(cardExpiry);

      // call backend to initialize trial and set plan with card details
      const res = await activateTrial({
        number: cardNumber,
        exp_month: month,
        exp_year: year,
        cvc: cardCvc,
        name: cardName || userName || "",
        address: {
          country: cardCountry,
          city: cardCity,
          street: cardStreet,
          zip: cardZip,
        },
      });

      if (!res || !res.data) {
        alert("Failed to activate free trial.");
        setIsActivatingTrial(false);
        setIsPageLoading(false);
        return;
      }

      // If request returned error status, show toast with message
      if (res.status && res.status >= 400) {
        const msg =
          res.data?.error || res.data?.message || "Failed to process card.";
        const details = res.data?.details ? ` (${res.data.details})` : "";
        showToastError(`${msg}${details}`);
        setIsActivatingTrial(false);
        setIsPageLoading(false);
        return;
      }

      // If 3DS redirect is required, keep loading and send user to Tap's verification page
      if (res.data?.requires3DS && res.data?.redirectUrl) {
        // Keep isPageLoading true until redirect happens - do not reset in finally
        // Redirect will navigate away before finally block runs
        window.location.href = res.data.redirectUrl;
        return;
      }

      // support both success and already-active responses
      const updatedUser = res.data.user || res.data;
      if (!updatedUser) {
        alert(
          res.data?.message || "Unexpected response while activating trial."
        );
        setIsActivatingTrial(false);
        setIsPageLoading(false);
        return;
      }

      // merge returned plan and trial into local user, keep existing token
      const mergedUser = {
        ...localUser,
        plan: updatedUser.plan || null,
        trial: updatedUser.trial || localUser.trial || null,
        role: updatedUser.role || localUser.role,
        name: updatedUser.name || localUser.name,
        email: updatedUser.email || localUser.email,
      };

      localStorage.setItem("user", JSON.stringify(mergedUser));

      // update toast to success
      showToastSuccess("Free trial activated!");

      // navigate based on role after trial activation
      if (mergedUser.role === "employee" || mergedUser.role === "admin") {
        navigate("/org/dashboard", { replace: true });
      } else {
        window.location.href = "/chat";
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to activate free trial.";
      showToastError(msg);
      setIsActivatingTrial(false);
      setIsPageLoading(false);
    }
  };

  // Remove manual skip trial action; expiration handled by backend status

  const handleBack = () => {
    navigate(-1);
  };

  // Handle subscription for expired trial users
  const handleSubscribeExpiredTrial = async () => {
    if (!plan) {
      showToastError("Plan not found");
      return;
    }

    const localUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (!localUser) {
      alert("Please sign in to subscribe.");
      window.location.href = "/login";
      return;
    }

    setIsPageLoading(true);

    try {
      // Call the create subscription checkout API
      // This will redirect to Tap checkout page
      const res = await createSubscriptionCheckout(
        plan._id,
        localUser._id,
        null,
        "tap",
        "monthly"
      );

      if (res?.data?.checkoutUrl) {
        // Store session ID if available for verification later
        if (res.data.sessionId) {
          localStorage.setItem("payment_session_id", res.data.sessionId);
        }
        // Redirect to Tap checkout
        window.location.href = res.data.checkoutUrl;
      } else {
        showToastError("Failed to create checkout session");
        setIsPageLoading(false);
      }
    } catch (error) {
      showToastError(
        error?.response?.data?.message || "Failed to initiate checkout"
      );
      setIsPageLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-bg-50">
      {/* Left Section */}
      <div className="w-full lg:w-1/2 bg-bg-50 flex flex-col justify-between p-6 md:p-12 overflow-y-auto">
        {/* Header with back arrow and logo */}
        <div className="max-w-md mx-auto w-full">
          <div className="flex items-center gap-3 mb-16">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-bg-60 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-fg-60" />
            </button>
            <LogoWithText
              className="h-7 w-auto max-w-32"
              fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
            />
          </div>

          {/* Plan Content */}
          {loading || loadingDiscount ? (
            <div className="space-y-6 animate-pulse">
              <div className="h-8 w-48 bg-bg-60 rounded"></div>
              <div className="h-6 w-32 bg-bg-60 rounded"></div>
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-4 w-full bg-bg-60 rounded"></div>
                ))}
              </div>
            </div>
          ) : plan ? (
            <>
              {/* Plan Type Badge */}
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-bg-70 text-fg-60 text-xs font-medium rounded-full">
                  {planType.charAt(0).toUpperCase() + planType.slice(1)} Plan
                </span>
              </div>

              {/* Pricing */}
              <div className="mb-12">
                <div className="mb-4">
                  <span className="text-fg-60 text-sm">Start at</span>
                </div>
                <div className="flex items-baseline gap-2 mb-6">
                  {hasDiscount ? (
                    <>
                      <span className="text-3xl font-bold text-fg-60 line-through mr-2">
                        ${originalPrice}
                      </span>
                      <span className="text-4xl font-bold text-fg-40">
                        ${discountedPrice}
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold text-fg-40">
                      ${originalPrice}
                    </span>
                  )}
                  <span className="text-fg-60 text-lg">/month</span>
                </div>

                {/* Discount Badge */}
                {hasDiscount && (
                  <div>
                    <span className="bg-green-500/20 text-green-600 px-3 py-1 rounded-full text-xs font-medium border border-green-500/30">
                      Save ${discountAmount}/month with referral discount!
                    </span>
                  </div>
                )}
              </div>

              {/* Usage Limits Table */}
              <div className="ml-1">
                <h4 className="text-fg-60 text-sm font-medium mb-6">
                  What's included
                </h4>

                {/* Table */}
                <div className="space-y-4 mb-4">
                  {/* Table header */}
                  <div className="grid grid-cols-3 gap-4 pb-4 border-b border-bd-50">
                    <div className="text-fg-60 text-xs font-medium">
                      Feature
                    </div>
                    <div className="text-fg-60 text-xs font-medium">Daily</div>
                    <div className="text-fg-60 text-xs font-medium">
                      Monthly
                    </div>
                  </div>

                  {/* Feature rows */}
                  <div className="space-y-3">
                    {features.map((feature, index) => (
                      <div
                        key={`limit-${index}`}
                        className="grid grid-cols-3 gap-4"
                      >
                        <div className="text-fg-60 text-sm">
                          {feature.label}
                        </div>
                        <div className="text-fg-40 text-sm font-normal">
                          {feature.daily}
                        </div>
                        <div className="text-fg-40 text-sm font-normal">
                          {feature.monthly}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-fg-50 mb-4">
                No Plans Available
              </h3>
              <p className="text-fg-60 text-sm">
                We're working on bringing you the best pricing plans. Please
                check back later.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-xs text-fg-60 pt-8 border-t border-bd-50 max-w-md mx-auto w-full">
          <p>Your payment is secure and encrypted.</p>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="w-full lg:w-1/2 bg-bg-70 flex items-center justify-center p-6 md:p-12 overflow-y-auto">
        {showExpiredTrialButton && (
          <div className="w-full max-w-md">
            <div className="bg-bg-60 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-fg-50 mb-4">
                {trial?.status === "cancelled"
                  ? "Trial Cancelled"
                  : "Trial Expired"}
              </h3>
              <p className="text-fg-60 text-sm mb-6">
                {trial?.status === "cancelled"
                  ? "Your free trial has been cancelled. Subscribe now to continue using all features."
                  : "Your free trial has ended. Subscribe now to continue using all features."}
              </p>

              <div className="space-y-3 mb-6 p-4 bg-bg-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-fg-60">Plan</span>
                  <span className="text-fg-50 font-medium">{plan?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-fg-60">Price</span>
                  <span className="text-fg-50 font-medium">
                    ${plan?.baseCost}/month
                  </span>
                </div>
                <div className="border-t border-bd-50 pt-3 flex justify-between text-sm font-medium">
                  <span className="text-fg-60">Total</span>
                  <span className="text-ac-02">${plan?.baseCost}/month</span>
                </div>
              </div>

              <button
                onClick={handleSubscribeExpiredTrial}
                disabled={isPageLoading}
                className="w-full bg-ac-02 text-white dark:text-black py-3 px-6 rounded-lg text-sm font-medium hover:bg-ac-01 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPageLoading ? "Processing..." : "Subscribe now"}
              </button>

              <p className="text-xs text-fg-60 text-center mt-4">
                You'll be redirected to secure checkout
              </p>
            </div>
          </div>
        )}
        {showFreeTrialButton && (
          <div className="w-full max-w-md">
            <h3 className="text-2xl font-bold text-fg-50 mb-8">
              Start Free Trial
            </h3>

            <form onSubmit={handleActivateTrial} className="space-y-6">
              {/* Card Number */}
              <div>
                <label className="block text-sm font-medium text-fg-50 mb-2">
                  Card number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  placeholder="1234 1234 1234 1234"
                  className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  required
                />
              </div>

              {/* Expiry and CVC */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-fg-50 mb-2">
                    Expiry date
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM/YY"
                    className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                    value={cardExpiry}
                    onChange={handleExpiryChange}
                    maxLength="5"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-50 mb-2">
                    CVC
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="123"
                    className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Name on Card */}
              <div>
                <label className="block text-sm font-medium text-fg-50 mb-2">
                  Name on card
                </label>
                <input
                  type="text"
                  placeholder="John Doe"
                  className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                />
              </div>

              {/* Country and City */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-fg-50 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    placeholder="United States"
                    className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                    value={cardCountry}
                    onChange={(e) => setCardCountry(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-50 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    placeholder="San Francisco"
                    className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                    value={cardCity}
                    onChange={(e) => setCardCity(e.target.value)}
                  />
                </div>
              </div>

              {/* Street */}
              <div>
                <label className="block text-sm font-medium text-fg-50 mb-2">
                  Street address
                </label>
                <input
                  type="text"
                  placeholder="123 Main St"
                  className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                  value={cardStreet}
                  onChange={(e) => setCardStreet(e.target.value)}
                />
              </div>

              {/* ZIP */}
              <div>
                <label className="block text-sm font-medium text-fg-50 mb-2">
                  ZIP / Postal code
                </label>
                <input
                  type="text"
                  placeholder="94105"
                  className="w-full bg-bg-70 text-fg-40 py-3 px-4 rounded-lg text-sm border border-bd-50 focus:outline-none focus:border-ac-02 placeholder-fg-80"
                  value={cardZip}
                  onChange={(e) => setCardZip(e.target.value)}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isActivatingTrial || !canSubmitCard()}
                className="w-full bg-ac-02 text-white dark:text-black py-3 px-6 rounded-lg text-sm font-medium hover:bg-ac-01 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-8"
              >
                {isActivatingTrial
                  ? "Processing..."
                  : "Start Free Trial (7 days)"}
              </button>

              {/* Security Note */}
              <p className="text-xs text-fg-60 text-center">
                Your payment information is secure and encrypted.
              </p>
            </form>
          </div>
        )}
      </div>

      {/* To uncomment - Payment Provider Selection Modal */}
      {/* <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSelectProvider={handleProviderSelection}
        plan={plan}
        discountAmount={discountAmount}
        loading={isRedirecting}
      /> */}

      {/* Full Page Loading Overlay */}
      {isPageLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-ac-02/30 border-t-ac-02 rounded-full animate-spin"></div>
            <p className="text-white text-sm font-medium">
              Processing your trial activation...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upgrade;
